import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { forkJoin } from 'rxjs';
import {
  DayOverrideRequest,
  CalendarDay,
  HolidayEventRef,
} from '../../model/holiday-calender.modal';
import { HolidayService } from '@service/holiday/holiday.service';
import Swal from 'sweetalert2';

interface EventRow extends HolidayEventRef {
  is_canceled: boolean;
  name?: string;
  event_reason?: string | null;
}

type RevertMode = 'OVERLAP' | 'HOLIDAY_ONLY' | 'WEEKEND_ONLY' | null;

@Component({
  selector: 'app-day-override-modal',
  templateUrl: './day-override-modal.component.html',
  styleUrls: ['./day-override-modal.component.css'],
})
export class DayOverrideModalComponent implements OnChanges {
  @Input() isVisible: boolean = false;
  @Input() selectedDay: CalendarDay | null = null;
  @Input() holidayYear: number;

  @Output() isVisibleChange = new EventEmitter<boolean>();
  @Output() overrideSuccess = new EventEmitter<void>();

  modalTitle: string = '';
  modalSubTitle: string = '';
  formattedDate: string = '';
  dayName: string = '';

  weekendRow: EventRow | null = null;
  /** All non-weekend holiday rows on this date (1..N). */
  holidayRows: EventRow[] = [];

  // Which revert UI to show when the date is currently a Working Day override
  revertMode: RevertMode = null;

  // Form controls
  revertOverlap: boolean = false; // OVERLAP case: single checkbox reverts weekend + all holidays
  revertToHoliday: boolean = false; // HOLIDAY_ONLY case
  revertToWeekend: boolean = false; // WEEKEND_ONLY case
  markAsWorkingDay: boolean = false;
  reasonInput: string = '';

  isSaving: boolean = false;

  constructor(private readonly holidayService: HolidayService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedDay'] && this.selectedDay) {
      const d = new Date(this.selectedDay.date);
      this.dayName = d.toLocaleDateString('en-US', { weekday: 'long' });
      this.formattedDate =
        d.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        }) + ` (${this.dayName})`;

      this.extractRows();

      // Reset form state on every open
      this.revertOverlap = false;
      this.revertToHoliday = false;
      this.revertToWeekend = false;
      this.markAsWorkingDay = false;
      this.reasonInput = '';
      this.isSaving = false;

      this.determineRevertMode();
      this.setHeaderTexts();
    }
  }

  /** Comma-separated holiday names for info text (latest-friendly display stays on calendar). */
  get holidayNamesLabel(): string {
    const names = this.holidayRows
      .map((r) => r.name)
      .filter((n): n is string => !!n && n.trim().length > 0);
    return names.length ? names.join(', ') : 'Holiday';
  }

  private extractRows(): void {
    this.weekendRow = null;
    this.holidayRows = [];

    const events: any[] = this.selectedDay?.rawApiEvents || [];

    events.forEach((e) => {
      const name = e.holiday_event_name?.trim() || '';
      const isWeekend = name.toLowerCase() === 'weekend';

      const isOthers = name.toLowerCase() === 'others';

      const displayName = isOthers ? e.event_reason?.trim() || name : name;

      const row: EventRow = {
        id: e.id,
        holiday_event_id: e.holiday_event_id,
        is_canceled: !!e.is_canceled,
        name: displayName,
        event_reason: e.event_reason ?? null,
      };

      if (isWeekend) {
        if (!this.weekendRow) {
          this.weekendRow = row;
        }
      } else {
        this.holidayRows.push(row);
      }
    });
  }

  // Decide which revert UI to show while the date is a Working Day override
  private determineRevertMode(): void {
    const weekendCanceled = !!this.weekendRow?.is_canceled;
    const anyHolidayCanceled = this.holidayRows.some((r) => r.is_canceled);

    if (weekendCanceled && anyHolidayCanceled) {
      this.revertMode = 'OVERLAP';
    } else if (anyHolidayCanceled) {
      this.revertMode = 'HOLIDAY_ONLY';
    } else if (weekendCanceled) {
      this.revertMode = 'WEEKEND_ONLY';
    } else {
      this.revertMode = null;
    }
  }

  private setHeaderTexts(): void {
    if (!this.selectedDay) return;

    if (this.selectedDay.isWorkingDayOverride) {
      if (this.revertMode === 'OVERLAP') {
        this.modalTitle = 'Working Day (Holiday & Weekend Override)';
      } else if (this.revertMode === 'WEEKEND_ONLY') {
        this.modalTitle = 'Working Day (Weekend Override)';
      } else {
        this.modalTitle = 'Working Day (Holiday Override)';
      }
    } else {
      this.modalTitle = this.selectedDay.isHoliday
        ? 'Holiday Details'
        : 'Weekend Holiday';
    }
  }

  private pushCanceledHolidays(targets: HolidayEventRef[]): void {
    this.holidayRows.forEach((row) => {
      if (row.is_canceled) {
        targets.push({
          id: row.id,
          holiday_event_id: row.holiday_event_id,
        });
      }
    });
  }

  private pushActiveHolidays(targets: HolidayEventRef[]): void {
    this.holidayRows.forEach((row) => {
      if (!row.is_canceled) {
        targets.push({
          id: row.id,
          holiday_event_id: row.holiday_event_id,
        });
      }
    });
  }

  private buildPayload(): DayOverrideRequest | null {
    if (!this.selectedDay) return null;

    let targets: HolidayEventRef[] = [];
    let actionType: 'MARK_AS_WORKING_DAY' | 'REVERT_TO_HOLIDAY';
    let isCanceled: boolean;

    if (this.selectedDay.isWorkingDayOverride) {
      // ---------- REVERT FLOW ----------
      actionType = 'REVERT_TO_HOLIDAY';
      isCanceled = false;

      if (this.revertMode === 'OVERLAP') {
        // Single checkbox -> revert weekend + every canceled holiday on this date
        if (!this.revertOverlap) return null;
        if (this.weekendRow?.is_canceled) {
          targets.push({
            id: this.weekendRow.id,
            holiday_event_id: this.weekendRow.holiday_event_id,
          });
        }
        this.pushCanceledHolidays(targets);
      } else if (this.revertMode === 'HOLIDAY_ONLY') {
        if (!this.revertToHoliday) return null;
        this.pushCanceledHolidays(targets);
      } else if (this.revertMode === 'WEEKEND_ONLY') {
        if (!this.revertToWeekend) return null;
        if (this.weekendRow?.is_canceled) {
          targets.push({
            id: this.weekendRow.id,
            holiday_event_id: this.weekendRow.holiday_event_id,
          });
        }
      }
    } else {
      // ---------- MARK AS WORKING DAY FLOW ----------
      if (!this.markAsWorkingDay) {
        return null;
      }
      if (!this.reasonInput.trim()) {
        Swal.fire({
          icon: 'error',
          title: 'Reason Required',
          text: 'Please provide a reason for marking this date as a working day.',
        });
        return null;
      }

      actionType = 'MARK_AS_WORKING_DAY';
      isCanceled = true;

      // Cancel every currently-active row for this date (weekend and/or all holidays)
      if (this.weekendRow && !this.weekendRow.is_canceled) {
        targets.push({
          id: this.weekendRow.id,
          holiday_event_id: this.weekendRow.holiday_event_id,
        });
      }
      this.pushActiveHolidays(targets);
    }

    if (targets.length === 0) return null;

    return {
      date: this.selectedDay.date,
      actionType,
      reason: this.reasonInput,
      is_canceled: isCanceled,
      targets,
    };
  }

  onSave(): void {
    if (this.isSaving) return;
    if (!this.holidayYear) {
      console.error('holidayYear input is missing on app-day-override-modal');
      return;
    }

    const payload = this.buildPayload();
    if (!payload) {
      this.closeModal();
      return;
    }

    this.runTargetRequests(payload);
  }

  private runTargetRequests(payload: DayOverrideRequest): void {
    const requests = payload.targets.map((target) =>
      this.holidayService.updateHoliday(
        this.holidayYear as number,
        target.holiday_event_id,
        target.id,
        {
          holiday_event_id: target.holiday_event_id,
          is_canceled: payload.is_canceled,
          cancelation_reason: payload.is_canceled ? payload.reason : '',
        },
      ),
    );

    this.isSaving = true;

    forkJoin(requests).subscribe(
      () => {
        this.isSaving = false;
        this.closeModal();
        this.overrideSuccess.emit();
      },
      (error) => {
        this.isSaving = false;
        console.error('Error updating holiday override:', error);
      },
    );
  }

  closeModal(): void {
    this.isVisible = false;
    this.isVisibleChange.emit(false);
  }
}
